import {ChangeDetectionStrategy, Component, OnInit, signal} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, Subject} from 'rxjs';
import {finalize, map, shareReplay, take} from 'rxjs/operators';
import {OnDestroyMixin} from '@w11k/ngx-componentdestroyed';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';

import {TerraUtils} from 'src/app/shared/TerraUtils';
import {UtilsService} from 'src/app/services/utils.service';
import {
  ContactImportResponse,
  InvalidRow
} from 'src/app/shared/models/ContactImportResponse.model';
import {UserService} from 'src/app/services/shared/user.service';
import {BaseResponseDto} from '../../../../shared/models/BaseResponseDto.model';
import { ImportContactService } from './import-contact.service';

@Component({
  selector: 'terra-investor-contact-import-dialog',
  templateUrl: './investor-contact-import-dialog.component.html',
  styleUrls: ['./investor-contact-import-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestorContactImportDialogComponent extends OnDestroyMixin implements OnInit {

  pageForm = this.fb.group({
    file: [null, Validators.required]
  });

  isSubmitted = false;
  disableSubmitted = signal(true);

  isLoading$ = new BehaviorSubject(false);
  isProcessing$ = new BehaviorSubject(false);
  public uploadEndpoint$ = this.importContactService.baseEndpoint$.pipe(map(b => `${b}upload`));

  private getContactImportTemplateResponse$ =  this.importContactService.getContactImportTemplate().pipe(shareReplay(1));

  private importResult$ = new Subject<ContactImportResponse>();
  private rawUserErrorsResponse$ = this.importResult$.pipe(
    map(result => result ? result.invalidRows : null), shareReplay(1));

  totalErrorsNumber$ = this.rawUserErrorsResponse$.pipe(map(allErrors => this.calcErrorsNumber(allErrors)),
    shareReplay(1));

  isShowAllErrors$ = new BehaviorSubject(false);

  displayShowAllErrorsButton$ = combineLatest([this.rawUserErrorsResponse$, this.isShowAllErrors$]).pipe(
    map(([allRowErrors, isShowAllErrors]) => allRowErrors && allRowErrors.length > 1 && !isShowAllErrors),
    shareReplay(1)
  );

  userErrors$ = combineLatest([this.rawUserErrorsResponse$, this.isShowAllErrors$])
  .pipe(
    map(([userErrors, isShowAllErrors]) => {
      if (userErrors) {
        return isShowAllErrors ? userErrors : userErrors.slice(0, 1);
      } else {
        return null;
      }
    }),
    shareReplay(1)
  );

  hasMoreErrorLines: boolean = false;

  supportedFileExtensions = TerraUtils.consts.supportedFileExtensions.CONTACTS_IMPORT;

  constructor(
    private importContactService: ImportContactService,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<InvestorContactImportDialogComponent>,
    private utilsService: UtilsService,
    private userService: UserService,
    private telemetryService: TelemetryService
  ) {
    super();
  }

  ngOnInit(): void {
  }

  downloadTemplate() {
    this.isLoading$.next(true);
    this.getContactImportTemplateResponse$
    .pipe(
      finalize(() => this.isLoading$.next(false))
    )
    .subscribe();
  }

  public submitImport = new Subject<void>();
  public submitImport$ = this.submitImport.asObservable();

  import(){
    this.submitImport.next();
  }

  onErrorImport(error){
    this.utilsService.alertErrorMessage(error?.error as BaseResponseDto);
  }

  onSuccessImport(res){
    this.telemetrySuccess();
    this.dialogRef.close(true);
  }
  
  private telemetrySuccess() {
    this.userService.accountDetails$.pipe(take(1))
    .subscribe(clientDetails => {
      this.telemetryService.create({
        eventID: '',
        eventTitle: 'CONTACTS IMPORT',
        additional: {
          organizationID: clientDetails.organizationDetails.id
        }
      }, AnalyticsServiceNameModel.Mixpanel | AnalyticsServiceNameModel.Insights);
    });
  }

  private calcErrorsNumber(invalidRows: InvalidRow[]): number {
    if (!invalidRows || invalidRows.length == 0 ) {
      return 0;
    }
    return invalidRows
    .map(row => row.errors.length)
    .reduce((totalErrors, currentRowErrorsNumber) => totalErrors + currentRowErrorsNumber);
  }
}
